/* src/components/ui/PaginationControls.js */
// Create this new file if you don't have a 'ui' folder

import React from 'react';

const PaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  totalItems,
  itemsPerPageOptions = [5, 10, 25, 50], // Default options
}) => {
  // Calculate the range of items being shown
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleSelectChange = (event) => {
    onItemsPerPageChange(Number(event.target.value));
  };

  // Don't render controls if there's only one page or less
  if (totalPages <= 1 && totalItems <= itemsPerPageOptions[0]) {
    return null;
  }

  return (
    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between bg-gray-50 px-4 py-3 border-t border-gray-200 rounded-b-lg">
      {/* Left Side: Items per page selector */}
      <div className="flex items-center mb-2 sm:mb-0">
        <label htmlFor="itemsPerPage" className="text-sm text-gray-600 mr-2">
          Show:
        </label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={handleSelectChange}
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-indigo-500 focus:border-indigo-500"
          aria-label="Items per page"
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-600 ml-2">per page</span>
      </div>

      {/* Right Side: Page info and Navigation */}
      <div className="flex items-center">
        {/* Page Info */}
        <p className="text-sm text-gray-700 mr-4">
          Showing <span className="font-medium">{startItem}</span>
          - <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> entries
        </p>

        {/* Navigation Buttons */}
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            aria-label="Previous page"
          >
            {/* Heroicon mini arrow-left */}
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages > 0 ? totalPages : 1}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages || totalPages === 0}
            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            aria-label="Next page"
          >
            {/* Heroicon mini arrow-right */}
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default PaginationControls;